<template>
    <div :class="['alert d-flex', typeClass]" style="gap: var(--space-2)">
        <!-- Icon Column -->
        <div class="flex-shrink-0 alert-circle-icon">
            <i class="fi fi-alert-circle fi-lg"></i>
        </div>

        <!-- Main Content Column -->
        <div
            class="flex-grow-1 flex-grow-item d-flex flex-column h-100 position-relative"
        >
            <!-- Header (optional) -->
            <div
                v-if="title"
                class="alert-title flex-shrink-0"
                style="margin-top: 0.15rem"
            >
                <p class="m-0">{{ title }}</p>
            </div>

            <!-- Body Content & Actions -->
            <div class="alert-content-row flex-grow-1">
                <div
                    class="alert-body flex-grow-1"
                    :class="{ 'body-text': !wrapBody }"
                >
                    <slot> </slot>
                </div>

                <!-- Action Controls -->
                <div class="action-buttons-container flex-shrink-0">
                    <template
                        v-for="button in actionButtons"
                        :key="button.click"
                    >
                        <button
                            v-if="button.asButton"
                            type="button"
                            class="btn btn-sm"
                            :class="button.class"
                            @click="handleAction(button)"
                        >
                            {{ button.label }}
                            <i
                                v-if="button.icon"
                                class="fi"
                                :class="button.icon"
                            />
                        </button>
                        <span
                            v-else
                            class="span-link"
                            @click="handleAction(button)"
                        >
                            {{ button.label }}
                            <i class="fi fi-chevron-right" />
                        </span>
                    </template>
                </div>
            </div>

            <!-- Clear Button (positioned absolutely) -->
            <button
                v-if="showClearBtn"
                class="clear-btn position-absolute icon-btn icon-btn-white icon-btn-sm"
                @click="$emit('clear')"
                aria-label="Close alert"
            >
                <i class="fi fi-x"></i>
            </button>
        </div>
    </div>
</template>

<script>
/**
 * A flexible alert component with configurable styling and actions
 * @emits {Event} clear - When dismiss button is clicked
 * @emits {Event} [button.clickEventName] - Custom events from action buttons
 */
export default {
    name: 'Alert',
    props: {
        /**
         * Determines alert color scheme and styling
         * @validValues ['INFO', 'URGENT', 'WARNING']
         */
        type: {
            type: String,
            default: 'INFO',
            validator(value) {
                // Define allowed values for type
                return ['INFO', 'URGENT', 'WARNING'].includes(value);
            },
        },
        /**
         * Optional title text for the alert
         */
        title: {
            type: String,
            default: '',
        },
        /**
         * Controls visibility of top-right dismiss button
         */
        showClearBtn: {
            type: Boolean,
            default: false,
        },
        wrapBody: {
            type: Boolean,
            default: false,
        },
        /**
         * Array of action buttons to display in footer
         * @example
         * [{
         *   click: 'submit',           // Event name to emit
         *   label: 'Submit',           // Button text
         *   icon: 'fi-check',          // Icon class
         *   class: 'btn-primary',      // Button style class
         *   asButton: true,            // Render action as a button instead of a link
         *   dismissOnAction: true      // Hide alert after click
         * }]
         */
        actionButtons: {
            type: Array,
            default: () => [],
            validator: (buttons) =>
                buttons.every(
                    (button) =>
                        typeof button.click === 'string' && // Event name to emit
                        typeof button.label === 'string' &&
                        (button.icon === undefined ||
                            typeof button.icon === 'string') &&
                        (button.class === undefined ||
                            typeof button.class === 'string') &&
                        (button.asButton === undefined ||
                            typeof button.asButton === 'boolean')
                ),
        },
    },
    emits: ['clear'], // [button.click]
    computed: {
        typeClass() {
            return `alert-${this.type}`;
        },
    },
    methods: {
        /**
         * Handles action button clicks
         * @param {Object} button - The button configuration object
         */
        handleAction(button) {
            this.$emit(button.click);
        },
    },
};
</script>

<style scoped>
.alert {
    align-items: start;
    padding: var(--space-3);
    border-radius: 0 var(--space-2) var(--space-2) 0;
    border: none;
    border-left: solid var(--space-2);
}

.alert-INFO {
    border-color: var(--primary-500);
    background-color: var(--primary-25);
}

.alert-INFO .alert-circle-icon {
    color: var(--primary-500);
}

.alert-WARNING {
    border-color: var(--warning);
    background-color: #f6f1de;
}

.alert-WARNING .alert-circle-icon {
    color: var(--warning);
}

.alert-URGENT {
    border-color: var(--danger);
    background-color: #eddcd7;
}
.alert-URGENT .alert-circle-icon {
    color: var(--danger);
}

.flex-grow-item {
    flex: 1;
    max-width: 100%; /* Prevents growing beyond parent's width */
    min-width: 0; /* Allows shrinking below content size if needed */
}

.alert-title {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.4;
    padding-top: 0.125rem;
    padding-right: var(--space-9);
}

.alert-body {
    padding-right: var(--space-6);
    color: var(--neutral-900);
    font-size: 0.875rem;
    line-height: var(--space-5);
    min-width: 0;
    width: 100%;
    padding-top: var(--space-1);
    padding-bottom: var(--space-2);
}

.body-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.body-text > * {
    display: inline; /* Force all child elements to inline display to truncate text */
}

.alert-content-row {
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

.action-buttons-container {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
}

.clear-btn {
    right: -0.25rem;
    top: -0.25rem;
    z-index: 3;
}

.span-link i {
    color: var(--primary-400);
}
</style>
